import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, RefreshCw, Database, Users, TrendingUp, Edit2, X, Save, Link, Copy, Check, ChevronUp, ChevronDown, LogOut, MousePointer, BarChart3, Image } from 'lucide-react';
import QuarterlyPerformancePanel from '../components/admin/QuarterlyPerformancePanel';
import AssetsGalleryPanel from '../components/admin/AssetsGalleryPanel';
import LinksPanel from '../components/admin/LinksPanel';
import { adminFetch, clearAdminToken } from '../lib/adminApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminResultsDashboard = () => {
  const navigate = useNavigate();
  
  // Check authentication on mount
  useEffect(() => {
    const isAuth = sessionStorage.getItem('aac_admin_auth');
    if (!isAuth) {
      navigate('/quantitative/results/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('aac_admin_auth');
    navigate('/quantitative/results/login');
  };

  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [blockchainTrades, setBlockchainTrades] = useState([]);
  const [forexTrades, setForexTrades] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [repairing, setRepairing] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({ total_clicks: 0, recent_clicks_7_days: 0, by_source: {}, by_button: {} });
  const [carouselSettings, setCarouselSettings] = useState({ speed: 0.8, initial_delay: 2000 });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [copiedContract, setCopiedContract] = useState(null);
  
  // Sort state: { column: 'date_open', direction: 'asc' | 'desc' }
  const [blockchainSort, setBlockchainSort] = useState({ column: 'date_open', direction: 'asc' });
  const [forexSort, setForexSort] = useState({ column: 'date_open', direction: 'asc' });
  const [clientSort, setClientSort] = useState({ column: 'start_date', direction: 'asc' });

  // Form states
  const [clientForm, setClientForm] = useState({ name: '', start_date: '', end_date: '', initial_amount: '' });
  const [blockchainForm, setBlockchainForm] = useState({ asset: '', trade_type: 'Long', contract: '', date_open: '', entry_price: '', date_close: '', exit_price: '', risked_percent: '', rr_ratio: '', comment: '', source_url: '', client_id: '' });
  const [forexForm, setForexForm] = useState({ asset: 'XAU/USD', trade_type: 'Buy', date_open: '', entry_price: '', date_close: '', exit_price: '', risked_percent: '', rr_ratio: '', comment: '', source_url: '', client_id: '' });
  const [payoutForm, setPayoutForm] = useState({ type: 'pips', img: '', alt: '', link: '', link_text: 'Verify →', order: 0, active: true });

  // Helper to parse date for sorting
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    const parts = dateStr.split('/');
    if (parts.length === 2) {
      return new Date(parseInt(parts[1]), parseInt(parts[0]) - 1, 1);
    } else if (parts.length === 3) {
      return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    }
    return new Date(0);
  };

  // Generic sort function
  const sortData = (data, column, direction) => {
    return [...data].sort((a, b) => {
      let valA = a[column];
      let valB = b[column];
      
      // Handle date columns
      if (column.includes('date')) {
        valA = parseDate(valA);
        valB = parseDate(valB);
      }
      // Handle numeric columns
      else if (typeof valA === 'number') {
        // Already numbers
      }
      // Handle string columns
      else {
        valA = (valA || '').toString().toLowerCase();
        valB = (valB || '').toString().toLowerCase();
      }
      
      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Toggle sort for a column
  const toggleSort = (currentSort, setSort, column) => {
    if (currentSort.column === column) {
      setSort({ column, direction: currentSort.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setSort({ column, direction: 'asc' });
    }
  };

  // Sortable header component
  const SortHeader = ({ label, column, sortState, onSort }) => (
    <th 
      className="text-left p-3 cursor-pointer hover:bg-gray-700 select-none"
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        <span className="flex flex-col">
          <ChevronUp size={12} className={sortState.column === column && sortState.direction === 'asc' ? 'text-cyan-400' : 'text-gray-600'} />
          <ChevronDown size={12} className={sortState.column === column && sortState.direction === 'desc' ? 'text-cyan-400' : 'text-gray-600'} style={{ marginTop: '-4px' }} />
        </span>
      </div>
    </th>
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const [clientsRes, blockchainRes, forexRes, analyticsRes, payoutsRes, settingsRes] = await Promise.all([
        adminFetch(`${API_URL}/api/results/clients`),
        adminFetch(`${API_URL}/api/results/blockchain-trades`),
        adminFetch(`${API_URL}/api/results/forex-trades`),
        adminFetch(`${API_URL}/api/analytics/clicks/summary`),
        adminFetch(`${API_URL}/api/payouts/all`),
        adminFetch(`${API_URL}/api/settings/carousel`)
      ]);
      setClients(await clientsRes.json());
      setBlockchainTrades(await blockchainRes.json());
      setForexTrades(await forexRes.json());
      setAnalyticsData(await analyticsRes.json());
      const payoutsData = await payoutsRes.json();
      setPayouts(Array.isArray(payoutsData) ? payoutsData : []);
      if (settingsRes.ok) {
        setCarouselSettings(await settingsRes.json());
      }
    } catch (error) {
      setMessage('Error loading data');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const showMessage = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedContract(id);
    setTimeout(() => setCopiedContract(null), 2000);
  };

  const resetForms = () => {
    setClientForm({ name: '', start_date: '', end_date: '', initial_amount: '' });
    setBlockchainForm({ asset: '', trade_type: 'Long', contract: '', date_open: '', entry_price: '', date_close: '', exit_price: '', risked_percent: '', rr_ratio: '', comment: '', source_url: '', client_id: '' });
    setForexForm({ asset: 'XAU/USD', trade_type: 'Buy', date_open: '', entry_price: '', date_close: '', exit_price: '', risked_percent: '', rr_ratio: '', comment: '', source_url: '', client_id: '' });
    setPayoutForm({ type: 'pips', img: '', alt: '', link: '', link_text: 'Verify →', order: 0, active: true });
    setEditingId(null);
  };

  // Client handlers
  const addOrUpdateClient = async (e) => {
    e.preventDefault();
    const method = editingId ? 'PUT' : 'POST';
    const url = editingId ? `${API_URL}/api/results/clients/${editingId}` : `${API_URL}/api/results/clients`;
    try {
      const res = await adminFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...clientForm, initial_amount: parseFloat(clientForm.initial_amount), end_date: clientForm.end_date || null })
      });
      if (res.ok) {
        showMessage(editingId ? 'Client updated!' : 'Client added!');
        resetForms();
        fetchData();
      }
    } catch (error) { showMessage('Error saving client'); }
  };

  const editClient = (client) => {
    setClientForm({ name: client.name, start_date: client.start_date, end_date: client.end_date || '', initial_amount: client.initial_amount.toString() });
    setEditingId(client.id);
  };

  const deleteClient = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    await adminFetch(`${API_URL}/api/results/clients/${id}`, { method: 'DELETE' });
    fetchData();
  };

  // Blockchain handlers
  const addOrUpdateBlockchainTrade = async (e) => {
    e.preventDefault();
    const method = editingId ? 'PUT' : 'POST';
    const url = editingId ? `${API_URL}/api/results/blockchain-trades/${editingId}` : `${API_URL}/api/results/blockchain-trades`;
    try {
      const res = await adminFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...blockchainForm, 
          risked_percent: parseFloat(blockchainForm.risked_percent), 
          rr_ratio: parseFloat(blockchainForm.rr_ratio), 
          contract: blockchainForm.contract || null,
          source_url: blockchainForm.source_url || null,
          client_id: blockchainForm.client_id || null
        })
      });
      if (res.ok) {
        showMessage(editingId ? 'Trade updated!' : 'Trade added!');
        resetForms();
        fetchData();
      }
    } catch (error) { showMessage('Error saving trade'); }
  };

  const editBlockchainTrade = (trade) => {
    setBlockchainForm({
      asset: trade.asset,
      trade_type: trade.trade_type,
      contract: trade.contract || '',
      date_open: trade.date_open,
      entry_price: trade.entry_price,
      date_close: trade.date_close,
      exit_price: trade.exit_price,
      risked_percent: trade.risked_percent.toString(),
      rr_ratio: trade.rr_ratio.toString(),
      comment: trade.comment || '',
      source_url: trade.source_url || '',
      client_id: trade.client_id || ''
    });
    setEditingId(trade.id);
  };

  const deleteBlockchainTrade = async (id) => {
    if (!window.confirm('Delete this trade?')) return;
    await adminFetch(`${API_URL}/api/results/blockchain-trades/${id}`, { method: 'DELETE' });
    fetchData();
  };

  // Forex handlers
  const addOrUpdateForexTrade = async (e) => {
    e.preventDefault();
    const method = editingId ? 'PUT' : 'POST';
    const url = editingId ? `${API_URL}/api/results/forex-trades/${editingId}` : `${API_URL}/api/results/forex-trades`;
    try {
      const res = await adminFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...forexForm, 
          entry_price: parseFloat(forexForm.entry_price), 
          exit_price: parseFloat(forexForm.exit_price), 
          risked_percent: parseFloat(forexForm.risked_percent), 
          rr_ratio: parseFloat(forexForm.rr_ratio),
          source_url: forexForm.source_url || null,
          client_id: forexForm.client_id || null
        })
      });
      if (res.ok) {
        showMessage(editingId ? 'Trade updated!' : 'Trade added!');
        resetForms();
        fetchData();
      }
    } catch (error) { showMessage('Error saving trade'); }
  };

  const editForexTrade = (trade) => {
    setForexForm({
      asset: trade.asset,
      trade_type: trade.trade_type,
      date_open: trade.date_open,
      entry_price: trade.entry_price.toString(),
      date_close: trade.date_close,
      exit_price: trade.exit_price.toString(),
      risked_percent: trade.risked_percent.toString(),
      rr_ratio: trade.rr_ratio.toString(),
      comment: trade.comment || '',
      source_url: trade.source_url || '',
      client_id: trade.client_id || ''
    });
    setEditingId(trade.id);
  };

  const deleteForexTrade = async (id) => {
    if (!window.confirm('Delete this trade?')) return;
    await adminFetch(`${API_URL}/api/results/forex-trades/${id}`, { method: 'DELETE' });
    fetchData();
  };

  // Payout handlers
  const addOrUpdatePayout = async (e) => {
    e.preventDefault();
    const method = editingId ? 'PUT' : 'POST';
    const url = editingId ? `${API_URL}/api/payouts/${editingId}` : `${API_URL}/api/payouts`;
    try {
      // Si img está vacío, usa el link para que el backend extraiga la imagen
      const dataToSend = {
        ...payoutForm,
        img: payoutForm.img || payoutForm.link,
        order: parseInt(payoutForm.order) || 0
      };
      const res = await adminFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });
      if (res.ok) {
        showMessage(editingId ? 'Payout updated!' : 'Payout added!');
        resetForms();
        fetchData();
      } else {
        const err = await res.json();
        showMessage('Error: ' + (err.detail || 'Unknown error'));
      }
    } catch (error) { showMessage('Error saving payout'); }
  };

  const editPayout = (payout) => {
    setPayoutForm({
      type: payout.type,
      img: '', // Dejar vacío, la imagen ya está guardada en el servidor
      alt: payout.alt,
      link: payout.link,
      link_text: payout.link_text,
      order: payout.order?.toString() || '0',
      active: payout.active !== false
    });
    setEditingId(payout.id);
  };

  const deletePayout = async (id) => {
    if (!window.confirm('Delete this payout?')) return;
    await adminFetch(`${API_URL}/api/payouts/${id}`, { method: 'DELETE' });
    fetchData();
  };

  const togglePayoutActive = async (payout) => {
    await adminFetch(`${API_URL}/api/payouts/${payout.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !payout.active })
    });
    fetchData();
  };

  const tabs = [
    { id: 'clients', label: 'Clients', icon: Users, count: clients.length },
    { id: 'blockchain', label: 'Blockchain', icon: Database, count: blockchainTrades.length },
    { id: 'forex', label: 'Forex', icon: TrendingUp, count: forexTrades.length },
    { id: 'performance', label: 'Rentabilidad', icon: BarChart3, count: null },
    { id: 'payouts', label: 'Payouts', icon: Image, count: payouts.length },
    { id: 'analytics', label: 'CTA Analytics', icon: MousePointer, count: analyticsData.total_clicks },
    { id: 'files', label: 'Archivos', icon: Image, count: null },
    { id: 'links', label: 'Links (/links)', icon: Link, count: null },
  ];

  const inputClass = "w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none";
  const labelClass = "block text-xs text-gray-400 mb-1";

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <a href="/quantitative/results" className="text-cyan-400 text-sm hover:text-cyan-300 mb-1 inline-block">← Back to Results</a>
            <h1 className="text-2xl font-bold">Results Dashboard</h1>
            <p className="text-gray-400 text-sm">Manage DAO clients and trades</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchData} disabled={loading} className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded text-sm">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 bg-gray-800 hover:bg-red-900/50 text-gray-400 hover:text-red-400 px-4 py-2 rounded text-sm transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        {/* Message */}
        {message && <div className="bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 px-4 py-2 rounded mb-4">{message}</div>}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); resetForms(); }} className={`flex items-center gap-2 px-4 py-2 rounded text-sm ${activeTab === tab.id ? 'bg-cyan-500 text-black' : 'bg-gray-800 hover:bg-gray-700'}`}>
              <tab.icon size={16} /> {tab.label} {tab.count !== null && <span className="bg-black/20 px-2 py-0.5 rounded text-xs">{tab.count}</span>}
            </button>
          ))}
        </div>

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="space-y-6">
            <form onSubmit={addOrUpdateClient} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                {editingId ? <><Edit2 size={18} /> Edit Client</> : <><Plus size={18} /> Add Client</>}
                {editingId && <button type="button" onClick={resetForms} className="ml-auto text-gray-400 hover:text-white"><X size={18} /></button>}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><label className={labelClass}>Name</label><input required className={inputClass} value={clientForm.name} onChange={e => setClientForm({...clientForm, name: e.target.value})} placeholder="CM-01" /></div>
                <div><label className={labelClass}>Start Date</label><input required className={inputClass} value={clientForm.start_date} onChange={e => setClientForm({...clientForm, start_date: e.target.value})} placeholder="DD/MM/YYYY" /></div>
                <div><label className={labelClass}>End Date (empty = active)</label><input className={inputClass} value={clientForm.end_date} onChange={e => setClientForm({...clientForm, end_date: e.target.value})} placeholder="DD/MM/YYYY" /></div>
                <div><label className={labelClass}>Initial Amount (€)</label><input required type="number" step="0.01" className={inputClass} value={clientForm.initial_amount} onChange={e => setClientForm({...clientForm, initial_amount: e.target.value})} placeholder="1200" /></div>
              </div>
              <button type="submit" className="mt-4 bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 rounded text-sm font-medium flex items-center gap-2">
                {editingId ? <><Save size={16} /> Update</> : <><Plus size={16} /> Add</>}
              </button>
            </form>
            <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-800">
                  <SortHeader label="Name" column="name" sortState={clientSort} onSort={(col) => toggleSort(clientSort, setClientSort, col)} />
                  <SortHeader label="Start" column="start_date" sortState={clientSort} onSort={(col) => toggleSort(clientSort, setClientSort, col)} />
                  <SortHeader label="End" column="end_date" sortState={clientSort} onSort={(col) => toggleSort(clientSort, setClientSort, col)} />
                  <SortHeader label="Amount" column="initial_amount" sortState={clientSort} onSort={(col) => toggleSort(clientSort, setClientSort, col)} />
                  <th className="p-3 w-24">Actions</th>
                </tr></thead>
                <tbody>
                  {sortData(clients, clientSort.column, clientSort.direction).map(c => (
                    <tr key={c.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                      <td className="p-3 font-medium">{c.name}</td>
                      <td className="p-3 text-gray-400">{c.start_date}</td>
                      <td className="p-3 text-gray-400">{c.end_date || <span className="text-green-400">Active</span>}</td>
                      <td className="p-3">€{c.initial_amount}</td>
                      <td className="p-3 flex gap-2">
                        <button onClick={() => editClient(c)} className="text-cyan-400 hover:text-cyan-300"><Edit2 size={16} /></button>
                        <button onClick={() => deleteClient(c.id)} className="text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                  {clients.length === 0 && <tr><td colSpan="5" className="p-4 text-center text-gray-500">No clients yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <QuarterlyPerformancePanel blockchainTrades={blockchainTrades} forexTrades={forexTrades} />
        )}

        {/* Files Tab */}
        {activeTab === 'files' && <AssetsGalleryPanel />}

        {activeTab === 'links' && <LinksPanel />}

        {/* Blockchain Tab */}
        {activeTab === 'blockchain' && (
          <div className="space-y-6">
            <form onSubmit={addOrUpdateBlockchainTrade} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                {editingId ? <><Edit2 size={18} /> Edit Blockchain Trade</> : <><Plus size={18} /> Add Blockchain Trade</>}
                {editingId && <button type="button" onClick={resetForms} className="ml-auto text-gray-400 hover:text-white"><X size={18} /></button>}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div><label className={labelClass}>Asset</label><input required className={inputClass} value={blockchainForm.asset} onChange={e => setBlockchainForm({...blockchainForm, asset: e.target.value})} placeholder="ETH, BTC..." /></div>
                <div><label className={labelClass}>Type</label><select className={inputClass} value={blockchainForm.trade_type} onChange={e => setBlockchainForm({...blockchainForm, trade_type: e.target.value})}><option>Long</option><option>Short</option></select></div>
                <div><label className={labelClass}>Client (optional)</label>
                  <select className={inputClass} value={blockchainForm.client_id} onChange={e => setBlockchainForm({...blockchainForm, client_id: e.target.value})}>
                    <option value="">All clients (global)</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div><label className={labelClass}>Contract (optional)</label><input className={inputClass} value={blockchainForm.contract} onChange={e => setBlockchainForm({...blockchainForm, contract: e.target.value})} placeholder="0x..." /></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div><label className={labelClass}>Date Open</label><input required className={inputClass} value={blockchainForm.date_open} onChange={e => setBlockchainForm({...blockchainForm, date_open: e.target.value})} placeholder="DD/MM/YYYY" /></div>
                <div><label className={labelClass}>Entry Price/MCap</label><input required className={inputClass} value={blockchainForm.entry_price} onChange={e => setBlockchainForm({...blockchainForm, entry_price: e.target.value})} placeholder="$2.5M or 2642" /></div>
                <div><label className={labelClass}>Date Close</label><input required className={inputClass} value={blockchainForm.date_close} onChange={e => setBlockchainForm({...blockchainForm, date_close: e.target.value})} placeholder="DD/MM/YYYY" /></div>
                <div><label className={labelClass}>Exit Price/MCap</label><input required className={inputClass} value={blockchainForm.exit_price} onChange={e => setBlockchainForm({...blockchainForm, exit_price: e.target.value})} placeholder="$5M or 2890" /></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div><label className={labelClass}>Risked %</label><input required type="number" step="0.1" className={inputClass} value={blockchainForm.risked_percent} onChange={e => setBlockchainForm({...blockchainForm, risked_percent: e.target.value})} placeholder="2.5" /></div>
                <div><label className={labelClass}>R:R Ratio (P&L)</label><input required type="number" step="0.01" className={inputClass} value={blockchainForm.rr_ratio} onChange={e => setBlockchainForm({...blockchainForm, rr_ratio: e.target.value})} placeholder="2.5 or -1" /></div>
                <div className="md:col-span-2"><label className={labelClass}>Source URL (optional)</label><input className={inputClass} value={blockchainForm.source_url} onChange={e => setBlockchainForm({...blockchainForm, source_url: e.target.value})} placeholder="https://x.com/..." /></div>
              </div>
              <div><label className={labelClass}>Comment (optional)</label><input className={inputClass} value={blockchainForm.comment} onChange={e => setBlockchainForm({...blockchainForm, comment: e.target.value})} placeholder="Trade notes..." /></div>
              <button type="submit" className="mt-4 bg-purple-500 hover:bg-purple-400 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2">
                {editingId ? <><Save size={16} /> Update</> : <><Plus size={16} /> Add</>}
              </button>
            </form>
            <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-800">
                  <SortHeader label="Asset" column="asset" sortState={blockchainSort} onSort={(col) => toggleSort(blockchainSort, setBlockchainSort, col)} />
                  <SortHeader label="Client" column="client_id" sortState={blockchainSort} onSort={(col) => toggleSort(blockchainSort, setBlockchainSort, col)} />
                  <SortHeader label="Type" column="trade_type" sortState={blockchainSort} onSort={(col) => toggleSort(blockchainSort, setBlockchainSort, col)} />
                  <SortHeader label="Open" column="date_open" sortState={blockchainSort} onSort={(col) => toggleSort(blockchainSort, setBlockchainSort, col)} />
                  <SortHeader label="Close" column="date_close" sortState={blockchainSort} onSort={(col) => toggleSort(blockchainSort, setBlockchainSort, col)} />
                  <SortHeader label="Risk%" column="risked_percent" sortState={blockchainSort} onSort={(col) => toggleSort(blockchainSort, setBlockchainSort, col)} />
                  <SortHeader label="R:R" column="rr_ratio" sortState={blockchainSort} onSort={(col) => toggleSort(blockchainSort, setBlockchainSort, col)} />
                  <th className="text-left p-3">Contract</th>
                  <th className="p-3 w-24">Actions</th>
                </tr></thead>
                <tbody>
                  {sortData(blockchainTrades, blockchainSort.column, blockchainSort.direction).map(t => (
                    <tr key={t.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                      <td className="p-3 font-medium">{t.asset}</td>
                      <td className="p-3 text-xs">{t.client_id ? clients.find(c => c.id === t.client_id)?.name || 'Unknown' : <span className="text-gray-500">Global</span>}</td>
                      <td className="p-3"><span className={`px-2 py-1 rounded text-xs ${t.trade_type === 'Long' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{t.trade_type}</span></td>
                      <td className="p-3 text-gray-400 text-xs">{t.date_open}</td>
                      <td className="p-3 text-gray-400 text-xs">{t.date_close}</td>
                      <td className="p-3">{t.risked_percent}%</td>
                      <td className={`p-3 font-bold ${t.rr_ratio >= 0 ? 'text-green-400' : 'text-red-400'}`}>{t.rr_ratio > 0 ? '+' : ''}{t.rr_ratio}</td>
                      <td className="p-3">{t.contract ? (
                        <button onClick={() => copyToClipboard(t.contract, t.id)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white font-mono bg-gray-800 px-2 py-1 rounded">
                          {t.contract.slice(0, 6)}...{t.contract.slice(-4)}
                          {copiedContract === t.id ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                        </button>
                      ) : <span className="text-gray-600">—</span>}</td>
                      <td className="p-3 flex gap-2">
                        <button onClick={() => editBlockchainTrade(t)} className="text-cyan-400 hover:text-cyan-300"><Edit2 size={16} /></button>
                        <button onClick={() => deleteBlockchainTrade(t.id)} className="text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                        {t.source_url && <a href={t.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300"><Link size={16} /></a>}
                      </td>
                    </tr>
                  ))}
                  {blockchainTrades.length === 0 && <tr><td colSpan="9" className="p-4 text-center text-gray-500">No trades yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Forex Tab */}
        {activeTab === 'forex' && (
          <div className="space-y-6">
            <form onSubmit={addOrUpdateForexTrade} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                {editingId ? <><Edit2 size={18} /> Edit Forex Trade</> : <><Plus size={18} /> Add Forex Trade</>}
                {editingId && <button type="button" onClick={resetForms} className="ml-auto text-gray-400 hover:text-white"><X size={18} /></button>}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div><label className={labelClass}>Asset</label><select className={inputClass} value={forexForm.asset} onChange={e => setForexForm({...forexForm, asset: e.target.value})}><option>XAU/USD</option><option>GBP/USD</option><option>EUR/USD</option><option>USD/JPY</option></select></div>
                <div><label className={labelClass}>Type</label><select className={inputClass} value={forexForm.trade_type} onChange={e => setForexForm({...forexForm, trade_type: e.target.value})}><option>Buy</option><option>Sell</option></select></div>
                <div><label className={labelClass}>Client (optional)</label>
                  <select className={inputClass} value={forexForm.client_id} onChange={e => setForexForm({...forexForm, client_id: e.target.value})}>
                    <option value="">All clients (global)</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div><label className={labelClass}>Date Open</label><input required className={inputClass} value={forexForm.date_open} onChange={e => setForexForm({...forexForm, date_open: e.target.value})} placeholder="DD/MM/YYYY" /></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div><label className={labelClass}>Entry Price</label><input required type="number" step="0.01" className={inputClass} value={forexForm.entry_price} onChange={e => setForexForm({...forexForm, entry_price: e.target.value})} placeholder="2822" /></div>
                <div><label className={labelClass}>Date Close</label><input required className={inputClass} value={forexForm.date_close} onChange={e => setForexForm({...forexForm, date_close: e.target.value})} placeholder="DD/MM/YYYY" /></div>
                <div><label className={labelClass}>Exit Price</label><input required type="number" step="0.01" className={inputClass} value={forexForm.exit_price} onChange={e => setForexForm({...forexForm, exit_price: e.target.value})} placeholder="2811" /></div>
                <div><label className={labelClass}>Risked %</label><input required type="number" step="0.1" className={inputClass} value={forexForm.risked_percent} onChange={e => setForexForm({...forexForm, risked_percent: e.target.value})} placeholder="0.83" /></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><label className={labelClass}>R:R Ratio (P&L)</label><input required type="number" step="0.01" className={inputClass} value={forexForm.rr_ratio} onChange={e => setForexForm({...forexForm, rr_ratio: e.target.value})} placeholder="2 or -1" /></div>
                <div className="md:col-span-3"><label className={labelClass}>Source URL (optional)</label><input className={inputClass} value={forexForm.source_url} onChange={e => setForexForm({...forexForm, source_url: e.target.value})} placeholder="https://..." /></div>
              </div>
              <div className="mt-4"><label className={labelClass}>Comment (optional)</label><input className={inputClass} value={forexForm.comment} onChange={e => setForexForm({...forexForm, comment: e.target.value})} placeholder="Trade notes..." /></div>
              <button type="submit" className="mt-4 bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 rounded text-sm font-medium flex items-center gap-2">
                {editingId ? <><Save size={16} /> Update</> : <><Plus size={16} /> Add</>}
              </button>
            </form>
            <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-800">
                  <SortHeader label="Asset" column="asset" sortState={forexSort} onSort={(col) => toggleSort(forexSort, setForexSort, col)} />
                  <SortHeader label="Client" column="client_id" sortState={forexSort} onSort={(col) => toggleSort(forexSort, setForexSort, col)} />
                  <SortHeader label="Type" column="trade_type" sortState={forexSort} onSort={(col) => toggleSort(forexSort, setForexSort, col)} />
                  <SortHeader label="Open" column="date_open" sortState={forexSort} onSort={(col) => toggleSort(forexSort, setForexSort, col)} />
                  <SortHeader label="Close" column="date_close" sortState={forexSort} onSort={(col) => toggleSort(forexSort, setForexSort, col)} />
                  <SortHeader label="Risk%" column="risked_percent" sortState={forexSort} onSort={(col) => toggleSort(forexSort, setForexSort, col)} />
                  <SortHeader label="R:R" column="rr_ratio" sortState={forexSort} onSort={(col) => toggleSort(forexSort, setForexSort, col)} />
                  <th className="p-3 w-24">Actions</th>
                </tr></thead>
                <tbody>
                  {sortData(forexTrades, forexSort.column, forexSort.direction).map(t => (
                    <tr key={t.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                      <td className="p-3 font-medium">{t.asset}</td>
                      <td className="p-3 text-xs">{t.client_id ? clients.find(c => c.id === t.client_id)?.name || 'Unknown' : <span className="text-gray-500">Global</span>}</td>
                      <td className="p-3"><span className={`px-2 py-1 rounded text-xs ${t.trade_type === 'Buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{t.trade_type}</span></td>
                      <td className="p-3 text-gray-400 text-xs">{t.date_open}</td>
                      <td className="p-3 text-gray-400 text-xs">{t.date_close}</td>
                      <td className="p-3">{t.risked_percent}%</td>
                      <td className={`p-3 font-bold ${t.rr_ratio >= 0 ? 'text-green-400' : 'text-red-400'}`}>{t.rr_ratio > 0 ? '+' : ''}{t.rr_ratio}</td>
                      <td className="p-3 flex gap-2">
                        <button onClick={() => editForexTrade(t)} className="text-cyan-400 hover:text-cyan-300"><Edit2 size={16} /></button>
                        <button onClick={() => deleteForexTrade(t.id)} className="text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                        {t.source_url && <a href={t.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300"><Link size={16} /></a>}
                      </td>
                    </tr>
                  ))}
                  {forexTrades.length === 0 && <tr><td colSpan="8" className="p-4 text-center text-gray-500">No trades yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payouts Tab */}
        {activeTab === 'payouts' && (
          <div className="space-y-6">
            {/* Carousel Settings */}
            <div className="bg-gradient-to-r from-yellow-900/30 to-gray-900 border border-yellow-600/30 rounded-lg p-4">
              <h3 className="font-bold mb-4 flex items-center gap-2 text-yellow-400">
                <RefreshCw size={18} /> Carousel Settings
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className={labelClass}>Velocidad (pixels/frame)</label>
                  <input 
                    type="number" 
                    step="0.1" 
                    min="0.1" 
                    max="5" 
                    className={inputClass} 
                    value={carouselSettings.speed} 
                    onChange={e => setCarouselSettings({...carouselSettings, speed: parseFloat(e.target.value) || 0.8})} 
                  />
                  <p className="text-xs text-gray-500 mt-1">Más bajo = más lento (0.5-1.0 recomendado)</p>
                </div>
                <div>
                  <label className={labelClass}>Delay inicial (ms)</label>
                  <input 
                    type="number" 
                    step="500" 
                    min="0" 
                    max="10000" 
                    className={inputClass} 
                    value={carouselSettings.initial_delay} 
                    onChange={e => setCarouselSettings({...carouselSettings, initial_delay: parseInt(e.target.value) || 2000})} 
                  />
                  <p className="text-xs text-gray-500 mt-1">Tiempo antes de empezar (2000 = 2 segundos)</p>
                </div>
                <button 
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await adminFetch(`${API_URL}/api/settings/carousel`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(carouselSettings)
                      });
                      if (res.ok) showMessage('Carousel settings saved!');
                    } catch (e) { showMessage('Error saving settings'); }
                  }}
                  className="bg-yellow-500 hover:bg-yellow-400 text-black px-4 py-2 rounded text-sm font-medium flex items-center gap-2 h-10"
                >
                  <Save size={16} /> Guardar Settings
                </button>
              </div>
            </div>

            <form onSubmit={addOrUpdatePayout} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                {editingId ? <><Edit2 size={18} /> Edit Payout</> : <><Plus size={18} /> Add Payout</>}
                {editingId && <button type="button" onClick={resetForms} className="ml-auto text-gray-400 hover:text-white"><X size={18} /></button>}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className={labelClass}>Type</label>
                  <select className={inputClass} value={payoutForm.type} onChange={e => setPayoutForm({...payoutForm, type: e.target.value})}>
                    <option value="pips">FundingPips</option>
                    <option value="hive">FundedHive NFT</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Label/Alt Text *</label>
                  <input required className={inputClass} value={payoutForm.alt} onChange={e => setPayoutForm({...payoutForm, alt: e.target.value})} placeholder="$56,715.20 or Hive #788" />
                </div>
                <div>
                  <label className={labelClass}>Order</label>
                  <input type="number" className={inputClass} value={payoutForm.order} onChange={e => setPayoutForm({...payoutForm, order: e.target.value})} placeholder="0" />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input type="checkbox" id="active" checked={payoutForm.active} onChange={e => setPayoutForm({...payoutForm, active: e.target.checked})} className="w-4 h-4" />
                  <label htmlFor="active" className="text-sm text-gray-300">Active</label>
                </div>
              </div>
              <div className="mb-4">
                <label className={labelClass}>Verification Link *</label>
                <input required className={inputClass} value={payoutForm.link} onChange={e => setPayoutForm({...payoutForm, link: e.target.value})} placeholder="https://app.fundingpips.com/certificates/verify/..." />
                <p className="text-xs text-gray-500 mt-1">Pega el link de verificación.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Image URL <span className="text-gray-500 font-normal">(opcional - si vacío, se extrae del link)</span></label>
                  <input className={inputClass} value={payoutForm.img} onChange={e => setPayoutForm({...payoutForm, img: e.target.value})} placeholder="https://... (dejar vacío para auto-extraer)" />
                </div>
                <div>
                  <label className={labelClass}>Link Button Text</label>
                  <input className={inputClass} value={payoutForm.link_text} onChange={e => setPayoutForm({...payoutForm, link_text: e.target.value})} placeholder="Verify →" />
                </div>
              </div>
              <button type="submit" className="mt-4 bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 rounded text-sm font-medium flex items-center gap-2">
                {editingId ? <><Save size={16} /> Update</> : <><Plus size={16} /> Add</>}
              </button>
            </form>

            {/* Preview */}
            {payoutForm.img && (
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <h4 className="text-sm text-gray-400 mb-3">Preview</h4>
                <div className="flex items-center gap-4">
                  <img src={payoutForm.img} alt={payoutForm.alt} className="w-32 h-auto rounded-lg" />
                  <div>
                    <p className="text-white font-medium">{payoutForm.alt || 'No label'}</p>
                    <p className="text-gray-500 text-xs">{payoutForm.type}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Payouts List */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
              <div className="p-4 border-b border-gray-800 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-bold">All Payouts ({payouts.length})</h3>
                  <p className="text-gray-500 text-xs mt-1">These appear in the carousel on the Quantitative page</p>
                </div>
                <button
                  onClick={async () => {
                    setRepairing(true);
                    try {
                      const res = await adminFetch(`${API_URL}/api/payouts/repair-images`, { method: 'POST' });
                      const data = await res.json();
                      alert(`Images repaired: ${data.repaired}. Could not fix: ${data.failed}`);
                      fetchData();
                    } finally {
                      setRepairing(false);
                    }
                  }}
                  disabled={repairing}
                  className="shrink-0 text-xs border border-gray-700 rounded px-3 py-2 hover:border-gray-400 disabled:opacity-50"
                  data-testid="repair-images-btn"
                >
                  {repairing ? 'Repairing...' : 'Repair images'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {payouts.map(p => (
                  <div key={p.id} className={`border rounded-lg p-3 ${p.active ? 'border-gray-700 bg-gray-800/50' : 'border-red-900/50 bg-red-900/10 opacity-60'}`}>
                    <div className="flex gap-3">
                      <img src={p.img} alt={p.alt} className="w-20 h-20 object-cover rounded" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{p.alt}</p>
                        <p className="text-xs text-gray-500">{p.type} • Order: {p.order}</p>
                        <p className={`text-xs mt-1 ${p.active ? 'text-green-400' : 'text-red-400'}`}>
                          {p.active ? '● Active' : '○ Inactive'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-700">
                      <button onClick={() => editPayout(p)} className="text-cyan-400 hover:text-cyan-300 text-xs flex items-center gap-1">
                        <Edit2 size={14} /> Edit
                      </button>
                      <button onClick={() => togglePayoutActive(p)} className={`text-xs flex items-center gap-1 ${p.active ? 'text-yellow-400 hover:text-yellow-300' : 'text-green-400 hover:text-green-300'}`}>
                        {p.active ? '○ Disable' : '● Enable'}
                      </button>
                      <button onClick={() => deletePayout(p.id)} className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1 ml-auto">
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
                {payouts.length === 0 && (
                  <div className="col-span-full text-center text-gray-500 py-8">
                    No payouts yet. Add your first one above!
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Summary Cards */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <BarChart3 className="text-green-400" size={24} />
                  <h3 className="font-bold text-lg">Total CTA Clicks</h3>
                </div>
                <div className="text-5xl font-bold text-green-400 mb-2">{analyticsData.total_clicks}</div>
                <p className="text-gray-400 text-sm">All "Invest Now" button clicks tracked</p>
              </div>
              
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <TrendingUp className="text-cyan-400" size={24} />
                  <h3 className="font-bold text-lg">Last 7 Days</h3>
                </div>
                <div className="text-5xl font-bold text-cyan-400 mb-2">{analyticsData.recent_clicks_7_days}</div>
                <p className="text-gray-400 text-sm">Recent clicks in the past week</p>
              </div>
            </div>

            {/* Clicks by Source */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <MousePointer size={20} className="text-purple-400" />
                Clicks by Source
              </h3>
              {Object.keys(analyticsData.by_source).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(analyticsData.by_source).map(([source, count]) => (
                    <div key={source} className="flex items-center justify-between bg-gray-800/50 p-3 rounded">
                      <span className="text-gray-300 font-medium">{source}</span>
                      <span className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded text-sm font-bold">{count} clicks</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No click data yet. Clicks will appear here when users click "Invest Now" buttons.</p>
              )}
            </div>

            {/* Clicks by Button */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Database size={20} className="text-yellow-400" />
                Clicks by Button ID
              </h3>
              {Object.keys(analyticsData.by_button).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(analyticsData.by_button).map(([button, count]) => (
                    <div key={button} className="flex items-center justify-between bg-gray-800/50 p-3 rounded">
                      <span className="text-gray-300 text-sm font-mono">{button}</span>
                      <span className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded text-sm font-bold">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No button-specific data yet.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminResultsDashboard;
